/**
 * Welcome Splash — the post-login micro-delight.
 *
 * Shown once per fresh sign-in for up to 10 seconds. Auto-continues to
 * the user's role-appropriate home dashboard, OR the user can click
 * "Enter MangoCubs" to skip ahead.
 *
 * Design goals:
 *   • Premium + playful, not childish.
 *   • Lightweight — pure CSS + Framer Motion (no Lottie / no Lottie
 *     library import). Total bundle impact: ~0 KB.
 *   • Co-branded — if the current branding context resolves to a school
 *     name, the welcome reads "Welcome to {School} powered by MangoCubs™".
 *     Otherwise "Welcome to MangoCubs™".
 *   • Role-aware — one short tip tailored to the signed-in role.
 *   • Accessible — honours prefers-reduced-motion (skips bounce, keeps
 *     the timer + button), keyboard ESC = continue.
 */
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/contexts/BrandingContext";
import { homeForUser } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";

const SPLASH_SECONDS = 10;

const ROLE_TIPS = {
  super_admin: "Manage franchises, branches, users, reports and platform settings from your dashboard.",
  hq_admin: "Compare branch performance, review franchise-wide analytics, and oversee your network.",
  franchise_hq_admin: "Compare branch performance, review franchise-wide analytics, and oversee your network.",
  branch_admin: "Start with today's enquiries, admissions, follow-ups and branch alerts.",
  principal: "Review attendance, teacher updates and today's learning progress.",
  centre_head: "Review attendance, teacher updates and today's learning progress.",
  academic_coordinator: "Check curriculum implementation and teacher task completion.",
  counsellor: "Review today's follow-ups and assigned enquiries.",
  teacher: "Begin with attendance, LMS tasks and today's learning update.",
  activity_trainer: "Plan today's activity, mark participation and share photos with parents.",
  father: "View your child's learning update, attendance and teacher notes.",
  mother: "View your child's learning update, attendance and teacher notes.",
  guardian: "View your child's learning update, attendance and teacher notes.",
  parent: "View your child's learning update, attendance and teacher notes.",
  medical_coordinator: "Check vaccination reminders, allergies and health alerts.",
  transport: "Review route, pickup and drop records for today.",
  transport_staff: "Review route, pickup and drop records for today.",
  non_teaching: "Check assigned tasks and branch announcements.",
  non_teaching_staff: "Check assigned tasks and branch announcements.",
  support: "Check assigned tasks and branch announcements.",
  support_staff: "Check assigned tasks and branch announcements.",
  inventory: "Track stock levels, dispatches and pending vendor orders.",
  inventory_staff: "Track stock levels, dispatches and pending vendor orders.",
  vendor: "Review assigned orders and delivery updates.",
  tech_coord: "Watch for branch support tickets and platform health alerts.",
  auditor: "Review compliance reports and recent audit logs.",
};

export default function Welcome() {
  const { user } = useAuth();
  const { brand, isCoBranded } = useBranding();
  const nav = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();
  const [remaining, setRemaining] = useState(SPLASH_SECONDS);
  const continued = useRef(false);

  const continueNow = () => {
    if (continued.current) return;
    continued.current = true;
    const dest = location.state?.next || homeForUser(user || {});
    nav(dest, { replace: true });
  };

  // Ticker — counts down once per second, fires continueNow at 0.
  useEffect(() => {
    if (!user) return; // hard-guard: shouldn't render without a user
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(id); continueNow(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ESC / Enter to skip.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        continueNow();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tip = ROLE_TIPS[user?.role] || "Let's get you started.";
  const firstName = (user?.name || "there").split(" ")[0];
  const schoolName = isCoBranded ? brand?.display_name : null;
  const showBrandLogo = isCoBranded && brand?.logo_url;
  const progressPct = ((SPLASH_SECONDS - remaining) / SPLASH_SECONDS) * 100;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center
                    bg-gradient-to-br from-paper via-mustard-soft/40 to-terracotta-soft/30
                    overflow-hidden"
         data-testid="welcome-splash">
      {/* Decorative confetti dots */}
      {!reduce && (
        <>
          <FloatingDot top="12%" left="14%" delay={0.0} color="#F58A1F" size={10} />
          <FloatingDot top="22%" left="84%" delay={0.4} color="#7CB342" size={14} />
          <FloatingDot top="74%" left="18%" delay={0.8} color="#E91E63" size={12} />
          <FloatingDot top="80%" left="80%" delay={1.2} color="#26A69A" size={10} />
          <FloatingDot top="35%" left="40%" delay={0.2} color="#F58A1F" size={6} />
          <FloatingDot top="62%" left="58%" delay={0.6} color="#F58A1F" size={8} />
        </>
      )}

      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: reduce ? 0 : 0.45, ease: "easeOut" }}
        className="relative z-10 max-w-md w-full mx-6 text-center"
      >
        {/* Logo block — co-brand or MangoCubs only */}
        <div className="mb-5">
          {showBrandLogo ? (
            <div className="flex flex-col items-center gap-2">
              <motion.img src={brand.logo_url} alt={schoolName}
                initial={reduce ? {} : { scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                className="w-20 h-20 rounded-2xl border-2 border-white shadow-floating object-cover" />
              <div className="text-xl font-bold font-heading text-ink">{schoolName}</div>
              <div className="text-[11px] uppercase tracking-wider text-ink-soft flex items-center gap-1.5">
                Powered by <Logo size={20} tagline={false} inline />
              </div>
            </div>
          ) : (
            <motion.div
              initial={reduce ? {} : { y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 180 }}
              className="inline-block">
              <Logo size={64} tagline={false} />
            </motion.div>
          )}
        </div>

        {/* Headline */}
        <motion.div
          initial={reduce ? {} : { y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.25 }}
          className="mb-4">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-mustard/15 border border-mustard/30 text-[11px] uppercase tracking-wider font-semibold text-mustard-deep mb-2">
            <Sparkle weight="fill" className="text-mustard-deep" /> Welcome back
          </div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-ink leading-tight">
            Hi {firstName}!
          </h1>
          <p className="text-sm text-ink-soft mt-1">
            {schoolName
              ? <>Welcome to <span className="font-semibold text-ink">{schoolName}</span> powered by MangoCubs™</>
              : <>Welcome to <span className="font-semibold text-ink">MangoCubs</span><sup className="text-[10px]">™</sup></>}
          </p>
        </motion.div>

        {/* Mascot bounce — pure CSS, no Lottie. */}
        <Mascot reduce={reduce} />

        {/* Role tip */}
        <motion.div
          initial={reduce ? {} : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-white border border-border rounded-2xl p-3.5 my-5 text-left flex gap-2.5 items-start shadow-soft"
          data-testid="welcome-tip">
          <span className="shrink-0 w-7 h-7 rounded-lg bg-sage-soft text-sage-deep flex items-center justify-center text-base">💡</span>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-sage-deep font-semibold">Tip for today</div>
            <div className="text-sm text-ink mt-0.5 leading-snug">{tip}</div>
          </div>
        </motion.div>

        {/* CTA + progress ring */}
        <motion.div
          initial={reduce ? {} : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="space-y-3">
          <button onClick={continueNow}
            data-testid="welcome-continue-btn"
            className="w-full h-12 rounded-full bg-terracotta text-white font-semibold flex items-center justify-center gap-2 hover:bg-terracotta-hover transition shadow-floating group">
            Enter MangoCubs
            <ArrowRight weight="bold" className="group-hover:translate-x-0.5 transition" />
          </button>

          <div className="flex items-center justify-center gap-2 text-[11px] text-ink-soft">
            <div className="relative w-32 h-1 bg-paper-alt rounded-full overflow-hidden">
              <div className="absolute inset-y-0 left-0 bg-terracotta transition-[width]"
                style={{ width: `${progressPct}%` }} />
            </div>
            <span data-testid="welcome-countdown">Auto-continue in {remaining}s</span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

function FloatingDot({ top, left, delay, color, size }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: [0, 0.7, 0.4], y: [20, -8, 4] }}
      transition={{ delay, duration: 2.4, repeat: Infinity, repeatType: "reverse" }}
      style={{ position: "absolute", top, left, width: size, height: size,
               background: color, borderRadius: "50%" }}
      aria-hidden="true"
    />
  );
}

function Mascot({ reduce }) {
  return (
    <div className="flex justify-center mb-1" aria-hidden="true">
      <motion.div
        initial={false}
        animate={reduce ? {} : { y: [0, -8, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        className="w-16 h-16 rounded-full bg-mustard/20 border-2 border-mustard flex items-center justify-center text-3xl">
        <span role="img" aria-label="mango">🥭</span>
      </motion.div>
    </div>
  );
}
